/**
 * 파일명 : cod0102m.tsx
 * 설명 : 메뉴 관리 화면
 * 수정내역
 ************************************************
 * Version    수정일자      수정자    수정내용      
 ************************************************
 * 1.0       2026-03-18   이경원     최초작성
 */

/************************************************
 * Import 영역  
 ************************************************/
/*  React  */
import React, { useReducer, useEffect } from "react";

/* Reducer */
import { useAppSelector, useAppDispatch } from '../../../redux/hooks';
import { Cod0102mAction } from '../../../redux/slice/cod/cod0102m_slice';
import { IMenuTreeState, IMenuState , IAuthState, IInquiryCndsState } from '../../../redux/models/cod/cod0102m_model';

/*  AntD  */
import { Collapse, Button, Row, Col, Layout, Space, Radio, Input, Typography, Tree, Table, Descriptions, Select } from "antd";
import { SearchOutlined, CaretRightOutlined, PlusOutlined, MinusOutlined, ArrowUpOutlined, ArrowDownOutlined } from "@ant-design/icons";

/*  업무  */
import * as CommonProxy from 'libs/CommonProxy';
import * as common      from 'libs/common';

/************************************************
 * 전역변수 영역
 ************************************************/
const Cod0102m = (props:any) => {
    /************************************************
     * 화면 초기화 영역  
     ************************************************/
    /* Render 변수 선언영역 */
    /* AntD */
    const { Content } = Layout;
    const { Text }    = Typography; 
    const { Panel }   = Collapse;

    /* Table Columns */
    const authColumns = React.useCallback(() => {
        const arr: any = [
            {
                title    : '권한ID',
                dataIndex: 'authrtyId',
                key      : 'authrtyId',
                width    : '40%',
                align    : 'center',
            },
            {
                title    : '권한명',
                dataIndex: 'authrtyNm',
                key      : 'authrtyNm',
                width    : '60%',
            },
        ];
        return arr;
    }, []);
    
	/** State / Ref / Reducer 선언 **/
    const dispatch         = useAppDispatch();
    const inquiryCnds      = useAppSelector((state) => state.Cod0102m.inquiryCnds);
    const menuTree         = useAppSelector((state) => state.Cod0102m.menuTree);
    const originMenuInfo   = useAppSelector((state) => state.Cod0102m.originMenuInfo);
    const menuInfo         = useAppSelector((state) => state.Cod0102m.menuInfo);
    const menuAuthMap      = useAppSelector((state) => state.Cod0102m.menuAuthMap);
    const authrtyIdList    = useAppSelector((state) => state.Cod0102m.authrtyIdList);
    const maxLvl           = useAppSelector((state) => state.Cod0102m.maxLvl);
    const selectedMenuId   = useAppSelector((state) => state.Cod0102m.selectedMenuId);
    const modifiedAuthMap  = useAppSelector((state) => state.Cod0102m.modifiedAuthMap);
    const currentAuthIds   = modifiedAuthMap[selectedMenuId] || []; //체크된 권한들 미리 변수로 뽑아놓기
    const [appliedAuthId, setAppliedAuthId] = React.useState('');   // 실제조회된권한 ID
    const isFiltered       = appliedAuthId !== '';

    const selectedMenu     = React.useMemo(() => {
        return menuInfo.find(item => item.menuId === selectedMenuId);
    }, [menuInfo, selectedMenuId]);  

    /**
    * 기능    : 화면 최초 로드시에 필요한 기능을 구현한다.
    * param  : 
    * return : None
    */
    React.useEffect(() => {
        //뎁스제한 설정 
        dispatch(Cod0102mAction.setMaxLvl('2'));
        //메뉴목록 조회
        fn_GetMenuTree();
        //권한목록 조회 
        fn_GetAuthList();
    }, []);

    /**
     * 기능   : 메뉴목록 조회
     * param : 
     * return : None
     */ 
    const fn_GetMenuTree = () => {
        // 입력파라미터
        const requestData:IInquiryCndsState = {
            ...inquiryCnds,
        };
    
        CommonProxy.callAsyncSvc(requestData, {
            txCd: 'TRCOMR0029',
            success: (responseData: any) => {
                //원본 비우기
                dispatch(Cod0102mAction.clearOriginInfo());
                //원본데이터
                dispatch(Cod0102mAction.setMenuInfo(responseData.menuItems));
                dispatch(Cod0102mAction.setMenuAuthMap(responseData.menuAuthMap));
                //트리데이터변환
                const treeData = buildMenuTree(responseData.menuItems);
                dispatch(Cod0102mAction.setMenuTree(treeData));    

                //초기 선택값 초기화
                dispatch(Cod0102mAction.setSelectedMenuId(''));
                //실제 조회된 조회조건 세팅
                setAppliedAuthId(requestData.authrtyId);
            },
        });
    };    
    
    /** 
     * 기능   : 권한목록 조회
     * param : 
     * return : None
     */ 
    const fn_GetAuthList = () => {
        const requestData = {};
    
        CommonProxy.callAsyncSvc(requestData, {
            txCd: 'TRCOMR0030',
            success: (responseData: any) => {

                //원본데이터
                dispatch(Cod0102mAction.setAuthrtyIdList(responseData.authItems));

                //조회조건 콤보박스용 가공 (label, value)
                const authOptions = [
                    { label: '전체', value: '' },
                    ...responseData.authItems.map((item: any) => ({
                            ...item,
                            label: item.authrtyNm, // 권한명 매핑
                            value: item.authrtyId  // 권한ID 매핑
                        }))
                ];

                //콤보박스저장
                dispatch(Cod0102mAction.setAuthrtyIdList(authOptions));

                // 초기 선택값 초기화
                dispatch(Cod0102mAction.clearModifiedAuthMap());
            },
        });
    };



    /************************************************
     * 이벤트 함수 영역
     ************************************************/
    /**
    * 기능   : onChange 이벤트 선언
    * 설명   : 상세 입력 필드 변경 이벤트 공통 핸들러
    * param : 필드명,필드값
    */
    const onChange = React.useCallback((e: any) => {
        if (!selectedMenu) return;

        let { name, value } = e.target;
        const originItem      = originMenuInfo.find(item => item.menuId === selectedMenuId); //원본데이터
        let   isSame          = false;

        //메뉴ID는 영문/숫자만 허용
        if (name === 'menuId') {
            // 영문 대소문자, 숫자 이외의 모든 문자를 제거
            const filteredValue = value.replace(/[^a-zA-Z0-9]/g, '');

            if (value !== filteredValue) {
                value = filteredValue;
            }
        }

        // 수정된 데이터를 원본과 비교
        if (originItem) {
            const currentStr = JSON.stringify({ ...selectedMenu, [name]: value?.toString().trim(), dirty: false });
            const originStr  = JSON.stringify({ ...originItem, dirty: false });
            isSame = (currentStr === originStr);
        }

        //전체리스트용 새 배열 
        const updatedMenuInfo = menuInfo.map(item => 
                                    item.menuId === selectedMenuId 
                                        ? { ...item, [name]: value, dirty: !isSame } //수정된 데이터 
                                          : item                                       //원본
                                );


        //메뉴트리반영
        refreshMenuState(updatedMenuInfo);

        //menuId가 수정됐을 경우 선택된 ID 상태도 반영 
        if (name === 'menuId') {
            dispatch(Cod0102mAction.setSelectedMenuId(value));
        }
    }, [selectedMenu, selectedMenuId, menuInfo, originMenuInfo]);
    
    /**
     * 기능   : authRowSelection 이벤트 선언
     * 설명   : 권한 테이블 체크박스 변경 이벤트 핸들러
     * param : selectedKeys (선택된 행의 키 배열)
     */
    const authRowSelection = {
        selectedRowKeys: currentAuthIds,
        onChange: (selectedKeys: React.Key[]) => {
            const newAuthIds = selectedKeys as string[];

            //체크된 권한 저장
            dispatch(Cod0102mAction.setModifiedAuthMap({ 
                menuId: selectedMenuId, 
                authIds: newAuthIds 
            }));

            
            const updatedMenuInfo = menuInfo.map(item => 
                                        item.menuId === selectedMenuId 
                                            ? { ...item, tmpAuths: newAuthIds } 
                                            : item
                                    );

            refreshMenuState(updatedMenuInfo);
        },
    };

    /**
     * 기능 : 입력 후 Enter key 입력 시 조회
     * 설명 : 입력컴포넌트
     * param : 
     * return : None
     */    
    const handleOnKeyUp = (e:any) => {
        if (e.key === 'Enter') {
            fn_GetMenuTree(); // 조회
        }
    };

    /**
     * 기능    : onClick 이벤트 선언
     * 섦여    : 공통 클릭 이벤트 핸들러
     * param  : 
     * return : None
     */
    const onClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        const buttonName = e.currentTarget.name;

        switch (buttonName) {
            case 'btnSearch'  : //조회
                fn_GetMenuTree();
                break;
                
            case 'btnAdd'     : //메뉴추가
                onAdd();
                break;
                
            case 'btnDelete'  : //메뉴삭제
                onDelete();
                break;

            case 'btnMoveUp'  : //메뉴이동
                onMoveUp();
                break;
                
            case 'btnMoveDown': //메뉴이동
                onMoveDown();
                break;

            case 'btnReset'   : //초기화 
                onReset(); 
                break;

            case 'btnSave'    : //저장
                onSave();
                break;

            default:
        }
    };

    /**
     * 기능 : onAdd 이벤트 선언
     * 설명 : 신규 메뉴를 추가한다.
     * return : None
     */
    const onAdd = () => {
        const limitLvl    = Number(maxLvl);      
        const tmpMenuId   = `NEW_${Date.now().toString().slice(-7)}`; 
        let   upperMenuId = '';
        let   upperMenuNm = '';
        let   newLvl      = 1;
        
        //기본값 설정 : 부모/레벨 결정 (최대메뉴레벨 2)
        if (selectedMenu) {
            const currentLvl = Number(selectedMenu.menuLvl);
            
            if (currentLvl >= limitLvl) {
                //현재 메뉴레벨: 2단계
                upperMenuId = selectedMenu.upperMenuId;
                upperMenuNm = selectedMenu.upperMenuNm;
                newLvl = currentLvl;
            } else {
                //현재 메뉴레밸:1단계
                upperMenuId = selectedMenu.menuId;
                upperMenuNm = selectedMenu.menuNm;
                newLvl = currentLvl + 1;
            }
        } else {
            const root = menuInfo.find(item => item.menuLvl === '1');

            if (root) {
                upperMenuId = root.upperMenuId; 
                upperMenuNm = root.upperMenuNm; 
            }
        } 

        // 2. 막내 번호 따오기
        const siblings = menuInfo.filter(item => item.upperMenuId === upperMenuId);
        const maxOrdr  = siblings.length > 0 ? Math.max(...siblings.map(s => Number(s.menuOrdr))) : 0;
                            
        // 2.신규메뉴 객체생성
        const newMenu: IMenuState = {
            id         : 0,      // 임시 ID (저장 시 DB에서 채번하거나 변경)
            instCd     : '000',
            menuId     : tmpMenuId,
            menuNm     : '신규 메뉴',
            upperMenuId: upperMenuId,
            upperMenuNm: upperMenuNm,
            menuLvl    : String(newLvl),
            menuOrdr   : maxOrdr + 1,
            useYn      : 'Y',
            isMobile   : 'N',
            isShow     : 'Y',
            added      : true, 
            dirty      : false 
        };

        // 3.화면 트리 데이터 갱신 : 현재 menuInfo에 newMenu를 합쳐서 다시 빌드
        const updatedMenuInfo = [...menuInfo, newMenu];
        refreshMenuState(updatedMenuInfo);

        // 5.새로 만든 메뉴를 자동 선택
        dispatch(Cod0102mAction.setSelectedMenuId(newMenu.menuId));

    };
    
    /**
     * 기능   : onDelete 이벤트 선언
     * param : 
     */
    const onDelete = () => {
        // 1.선택된 메뉴가 없는 경우 체크
        if (!selectedMenuId || !selectedMenu) {
            const param = {
                contents: '삭제할 메뉴를 선택해주세요.',
            };
            common.openInfo(param);
            return;
        }

        // 2.신규 메뉴 여부 확인
        if (selectedMenu.added) {
            // [신규 메뉴 삭제 로직]
            // 2-1.menuInfo 배열에서 현재 선택된 ID만 제외하고 필터링
            const updatedMenuInfo = menuInfo.filter(item => item.menuId !== selectedMenuId);
            
            // 2-2.화면갱신
            refreshMenuState(updatedMenuInfo);

            // 2-3.초기 선택값 초기화
            dispatch(Cod0102mAction.setSelectedMenuId(''));
            dispatch(Cod0102mAction.clearModifiedAuthMap());
        } else {
            const param = {
                contents: '이미 저장된 메뉴는 삭제할 수 없습니다.\n [사용여부]를 "N"으로 설정하여 관리해주세요.',
            };
            common.openInfo(param);
            return;
        }
    };

    /**
     * 기능   : onMoveUp 이벤트 선언
     * param : 
     */
    const onMoveUp = () => {
        // 선택된 메뉴가 없는 경우 체크
        if (!selectedMenuId || !selectedMenu) {
            const param = {
                contents: '이동할 메뉴를 선택해주세요.',
            };
            common.openInfo(param);
            return;
        }

        // 1.부모 공유하는 형제 노드 추출하여 정렬 
        const siblings = menuInfo.filter(item => item.upperMenuId === selectedMenu.upperMenuId)
                                 .sort((a, b) => Number(a.menuOrdr) - Number(b.menuOrdr));

        // 2.현재 내 위치(Index) 파악
        const currentIndex = siblings.findIndex(item => item.menuId === selectedMenuId);
        
        // 3.상향이동 로직 시작
        let updatedMenuInfo = null;
        if (currentIndex === 0) {     
            //-----------------------------
            // 3-1.최상위 메뉴일경우(레벨업 이동)
            //-----------------------------
            // 1.현재 메뉴 lvl 1단계 : 이동 불가 (메뉴lvl 2단계부터 이동가능)
            if (selectedMenu.menuLvl === '1') return;                                                                                                                                                                                                                                                   
            
            // 2.메뉴 lvl 1단계 목록 및 형제 조회
            const parentSiblings = menuInfo.filter(item => item.menuLvl === '1')
                                           .sort((a, b) => Number(a.menuOrdr) - Number(b.menuOrdr));
            // 3.부모(1단계)정보 조회 및 위치 찾기 
            const myParent = menuInfo.find(item => item.menuId === selectedMenu.upperMenuId);
            if (!myParent) return;
            const parentIndex = parentSiblings.findIndex(item => item.menuId === myParent.menuId);

            if (parentIndex === 0) {
                //-----------------------------
                // 4-1. 부모가 1단계 첫 번째라면 -> 나를 최상위(ROOT)로 이동
                //-----------------------------
                // 1단계 메뉴들 중 현재 가장 작은 Ordr 값을 찾음
                const minOrdr = Math.min(...parentSiblings.map(s => Number(s.menuOrdr)));
                const oldUpperMenuId = selectedMenu.upperMenuId; // 내가 떠나온 부모 ID 저장
                let tempMenuInfo = menuInfo.map(item => {
                    if (item.menuId === selectedMenuId) {
                        return {
                            ...item,
                            upperMenuId: myParent.upperMenuId || '', 
                            upperMenuNm: myParent.upperMenuNm || 'ROOT',
                            menuLvl    : '1',         
                            menuOrdr   : minOrdr - 1,         
                            dirty    : true        
                        };
                    }
                    return item;
                });

                // 1. 내가 빠져나온 원래 부모 그룹 정렬 
                tempMenuInfo = getReorderedList(tempMenuInfo, oldUpperMenuId);

                // 2. 내가 새로 들어간 최상위(ROOT) 그룹 정렬 (minOrdr-1 보정)
                updatedMenuInfo = getReorderedList(tempMenuInfo, myParent.upperMenuId);
            } else {
                //-----------------------------
                // 4-2.부모가 전체 메뉴 중 최상위가 아니라면 -> 부모의 이전 형제(targetParent)의 마지막 자식으로 이동
                //-----------------------------
                const targetParent   = parentSiblings[parentIndex - 1]; 
                const targetChildren = menuInfo.filter(item => item.upperMenuId === targetParent.menuId);
                const oldUpperMenuId = selectedMenu.upperMenuId; // 이동 전 부모 ID 저장
                //타겟 메뉴(lvl 1단계)의 하위메뉴(lvl 2단계)중 max
                const maxOrdr = targetChildren.length > 0 ? Math.max(...targetChildren.map(c => Number(c.menuOrdr))) : 0;
        
                //타겟 부모의 마지막 순번(maxOrdr + 1)으로 이동
                let tempMenuInfo = menuInfo.map(item => {
                    if (item.menuId === selectedMenuId) {
                        return {
                            ...item,
                            upperMenuId: targetParent.menuId, 
                            upperMenuNm: targetParent.menuNm,
                            menuLvl: '2',                    
                            menuOrdr: maxOrdr + 1,           
                            dirty: true
                        };
                    }
                    return item;
                });

                //빠져나간 기존 부모 그룹 재정렬
                updatedMenuInfo = getReorderedList(tempMenuInfo, oldUpperMenuId);
            }

        } else {     
            //-----------------------------
            // 3-2.최상위 메뉴가 아닌경우(위쪽 형제와 위치 교체))
            //-----------------------------
            // 1.부모 공유하는 형제 노드들 중 max 
            const targetMenu  = siblings[currentIndex - 1];
            const myChildren  = menuInfo.filter(c => c.upperMenuId === selectedMenu.menuId);

            // Lvl 1이면서 자식이 하나도 없을 때만 형님의 하위로 들어감
            if (selectedMenu.menuLvl === '1' && myChildren.length === 0) {
                const targetChildren = menuInfo.filter(c => c.upperMenuId === targetMenu.menuId);
                const maxOrdr = targetChildren.length > 0 ? Math.max(...targetChildren.map(c => Number(c.menuOrdr))) : 0;

                updatedMenuInfo = menuInfo.map(item => 
                    item.menuId === selectedMenuId 
                    ? { 
                        ...item, 
                        upperMenuId: targetMenu.menuId, 
                        upperMenuNm: targetMenu.menuNm, 
                        menuLvl: '2', 
                        menuOrdr: maxOrdr + 1, 
                        dirty: true 
                    }
                    : item
                );
            } 
            // 자식이 있는 Lvl 1이거나, 이미 Lvl 2인 경우 -> 단순 1:1 위치 교체
            else {
                // 2. 현재 메뉴와 위쪽 메뉴의 순서(Ordr)를 서로 교체
                const currentOrdr = selectedMenu.menuOrdr;
                const targetOrdr = targetMenu.menuOrdr;

                updatedMenuInfo = menuInfo.map(item => {
                    // current -> target
                    if (item.menuId === selectedMenuId) return { ...item, menuOrdr: targetOrdr, dirty: true };
                    // target  -> current
                    if (item.menuId === targetMenu.menuId) return { ...item, menuOrdr: currentOrdr, dirty: true };
                    return item;
                });
            }
        }
        if (updatedMenuInfo) refreshMenuState(updatedMenuInfo);
    };
     
    /**
     * 기능   : onMoveDown 이벤트 선언
     * param : 
     */
    const onMoveDown = () => {
        // 선택된 메뉴가 없는 경우 체크
        if (!selectedMenuId || !selectedMenu) {
            const param = {
                contents: '이동할 메뉴를 선택해주세요.',
            };
            common.openInfo(param);
            return;
        }

        // 1. 부모를 공유하는 형제 노드 추출하여 정렬
        const siblings = menuInfo.filter(item => item.upperMenuId === selectedMenu.upperMenuId)
                                .sort((a, b) => Number(a.menuOrdr) - Number(b.menuOrdr));

        // 2. 현재 내 위치(Index) 파악
        const currentIndex = siblings.findIndex(item => item.menuId === selectedMenuId);
        
        let updatedMenuInfo = null;

        // ---------------------------------------------------------
        // CASE 1: 현재 그룹 내에서 '마지막' 순서일 때 (레벨 이동)
        // ---------------------------------------------------------
        if (currentIndex === siblings.length - 1) {
            // 현재 메뉴 lvl 1단계면 더 이상 내려갈 곳이 없음 (이동 불가)
            if (selectedMenu.menuLvl === '1') return;

            // 1. lvl 1단계 목록 조회
            const parentSiblings = menuInfo.filter(item => item.menuLvl === '1')
                                        .sort((a, b) => Number(a.menuOrdr) - Number(b.menuOrdr));
            
            // 2. 부모(1단계) 정보 조회 및 위치 찾기
            const myParent = menuInfo.find(item => item.menuId === selectedMenu.upperMenuId);
            if (!myParent) return;
            const parentIndex = parentSiblings.findIndex(item => item.menuId === myParent.menuId);

            // 부모가 마지막 대메뉴라면 더 이상 내려갈 '아랫집'이 없음
            if (parentIndex === parentSiblings.length - 1) return;

            // 3. 부모의 다음 형제(targetParent)의 '첫 번째' 자식으로 이동
            const targetParent = parentSiblings[parentIndex + 1];
            const targetChildren = menuInfo.filter(item => item.upperMenuId === targetParent.menuId);
            // 타겟의 자식들 중 가장 작은 순서보다 앞(-1)으로 설정
            const minOrdr = targetChildren.length > 0 ? Math.min(...targetChildren.map(c => Number(c.menuOrdr))) : 1;

            let tempMenuInfo = menuInfo.map(item => {
                if (item.menuId === selectedMenuId) {
                    return {
                        ...item,
                        upperMenuId: targetParent.menuId,
                        upperMenuNm: targetParent.menuNm,
                        menuLvl: '2',
                        menuOrdr: minOrdr - 1, // 첫 번째로 안착
                        dirty: true
                    };
                }
                return item;
            });
            // 재정렬 
            updatedMenuInfo = getReorderedList(tempMenuInfo, targetParent.menuId);      
        } 
        // ---------------------------------------------------------
        // CASE 2: 아래에 형제가 있을 때 (위치 교체 또는 하위 편입)
        // ---------------------------------------------------------
        else {
            const targetMenu = siblings[currentIndex + 1]; // 바로 아래 동생
            const myChildren = menuInfo.filter(c => c.upperMenuId === selectedMenu.menuId);

            // [핵심] Lvl 1이면서 자식이 없는 경우 -> 아래 형제의 '첫 번째' 자식으로 쏙
            if (selectedMenu.menuLvl === '1' && myChildren.length === 0) {
                const targetChildren = menuInfo.filter(c => c.upperMenuId === targetMenu.menuId);
                const minOrdr = targetChildren.length > 0 ? Math.min(...targetChildren.map(c => Number(c.menuOrdr))) : 1;

                let tempMenuInfo = menuInfo.map(item => 
                    item.menuId === selectedMenuId 
                    ? { 
                        ...item, 
                        upperMenuId: targetMenu.menuId, 
                        upperMenuNm: targetMenu.menuNm, 
                        menuLvl: '2', 
                        menuOrdr: minOrdr - 1, 
                        dirty: true 
                    }
                    : item
                );
                
                // lvl1 그룹에서 빠져나갔으므로 원래 부모(lvl1) 정렬 추가 
                tempMenuInfo = getReorderedList(tempMenuInfo, selectedMenu.upperMenuId); 
                updatedMenuInfo = getReorderedList(tempMenuInfo, targetMenu.menuId); 
            } else {
                // 일반 위치 교체 (자식이 있는 Lvl 1이거나 Lvl 2인 경우)
                const currentOrdr = selectedMenu.menuOrdr;
                const targetOrdr = targetMenu.menuOrdr;

                updatedMenuInfo = menuInfo.map(item => {
                    if (item.menuId === selectedMenuId) return { ...item, menuOrdr: targetOrdr, dirty: true };
                    if (item.menuId === targetMenu.menuId) return { ...item, menuOrdr: currentOrdr, dirty: true };
                    return item;
                });
            }
        }

        if (updatedMenuInfo) refreshMenuState(updatedMenuInfo);
    };

    /**
     * 기능   : onReset 이벤트 선언
     * 설명   : 결과초기화 이벤트 선언
     * param : 
     */
    const onReset = () => {
         const param = {
            title   : '위드정보 확인',
            contents: '수정중인 모든 내용을 초기화하시겠습니까?',
            accept: () => {
                //수정된 권한 초기화
                dispatch(Cod0102mAction.clearModifiedAuthMap());

                //조회 조건 초기화 (Select 박스 선택 해제)
                dispatch(Cod0102mAction.setInquiryCnds({ 
                    ...inquiryCnds, // 기존에 selector로 가져온 조회조건 변수명
                    authrtyId: '' 
                }));

                //원본으로 엎어치기
                if (originMenuInfo && originMenuInfo.length > 0) {
                    dispatch(Cod0102mAction.setMenuInfo(originMenuInfo));
                    //트리에 반영
                    refreshMenuState([...originMenuInfo]);
                    //선택된 메뉴 초기화
                    dispatch(Cod0102mAction.setSelectedMenuId(''));
                }
            }
        };
        common.openConfirm(param);
    };

    /**
     * 기능   : onSave 이벤트 선언
     * param : 
     */
    const onSave = () => {
        const saveList   = menuInfo.filter(item => item.dirty || item.added);
        const hasChanges = saveList.length > 0;

        //정합성체크
        for (const item of saveList) {
            let errorMsg = '';

            // 1. 필수 입력 체크
            if (!item.menuId?.trim())      errorMsg = '[메뉴ID]는 필수 입력항목입니다.';
            else if (!item.menuNm?.trim()) errorMsg = '[메뉴명]은 필수 입력항목입니다.';
            
            // 2. 글자수 제한 체크
            else if (item.menuId.length > 11)  errorMsg = '메뉴ID는 11자 이내로 입력해주세요.';
            else if (item.menuNm.length > 100) errorMsg = '메뉴명은 100자 이내로 입력해주세요.';
            else if (item.pgmId && item.pgmId.length > 20) errorMsg = '프로그램ID는 20자 이내로 입력해주세요.';

            if (errorMsg) {
                common.openInfo({ contents: errorMsg });
                return;
            }
        }

        //신규 또는 수정 항목 체크
        if (!hasChanges) {
           const param = {
                contents: '저장할 내역이 없습니다.',
            };
            common.openInfo(param);
            return;
        } else {
            const param = {
                title   : '위드정보 확인',
                contents: '저장하시겠습니까?',
                accept: () => {
                    
                    //입력파라미터
                    const requestData = {
                        menuList: saveList,        // 수정/신규 메뉴 리스트
                        authMap : modifiedAuthMap  // 변경된 권한 매핑 정보
                    };
            
                    CommonProxy.callAsyncSvc(requestData, {
                        txCd : 'TRCOMS0024',
                        success : (_responseData:any) => {
                            const param = {
                                title : '위드정보 확인',
                                contents: '저장 완료 되었습니다.',
                                accept: () => {
                                    fn_GetMenuTree(); // 메뉴 트리 다시 그리기
                                    fn_GetAuthList(); // 권한 목록 다시 가져오기
                                }
                            };
                            common.openAlert(param);

                        },
                    });
                }
            };
            common.openConfirm(param);
        }
    };

    /************************************************
     * 개발자함수 영역
     ************************************************/
    /**
     * 기능    : 메뉴 목록 데이터를 Tree 구조로 변환한다. (ROOT 제외)
     * param  : list (메뉴 목록 배열)
     * return : Tree 구조 데이터
     */
    const buildMenuTree = (list: any[]): IMenuTreeState[] => {
        const map = new Map();
        const tree: any[] = [];

        //ROOT 제거 및 정렬
        const filtered = list.filter(item => item.menuNm !== 'ROOT')
                             .sort((a, b) => Number(a.menuOrdr) - Number(b.menuOrdr));
        //노드 생성
        filtered.forEach(item => {
            map.set(item.menuId, { key        : item.menuId,
                                   title      : item.menuNm,
                                   menuId     : item.menuId,
                                   upperMenuId: item.upperMenuId,
                                   menuOrdr   : item.menuOrdr, 
                                   useYn      : item.useYn,
                                   isShow     : item.isShow,
                                   children   : [],
            });
        });

        //부모 연결
        filtered.forEach(item => {
            const node = map.get(item.menuId);

            if (item.upperMenuId && map.has(item.upperMenuId)) {
                const parent = map.get(item.upperMenuId);
                parent.children.push(node);
            } else {
                tree.push(node);
            }
        });

        return tree;
    };

    /**
     * 기능   : 메뉴 정보와 트리 구조를 동시에 갱신하는 공통 함수
     * param : updatedMenuInfo - 변경된 전체 메뉴 배열
     */
    const refreshMenuState = (updatedMenuInfo: any[]) => {
        if (!updatedMenuInfo) return;

        const finalData = updatedMenuInfo.map(item => {
            // 공통 Trim 처리
            const trimmedItem = {
                ...item,
                menuId: item.menuId?.trim(),
                menuNm: item.menuNm?.trim(),
                pgmId : item.pgmId?.trim(),
            };

            // 신규 추가된 행인 경우
            if (trimmedItem.added) {
                return { ...trimmedItem, dirty: false }; 
            }

            // 기존 행인 경우 (원본과 비교)
            const originItem = originMenuInfo.find(origin => origin.menuId === trimmedItem.menuId);

            if (originItem) {
                //메뉴 정보 비교
                const originCompare = {
                    upperMenuId : originItem.upperMenuId     || null,
                    menuId      : originItem.menuId?.trim()  || '',
                    menuNm      : originItem.menuNm?.trim()  || '',
                    pgmId       : originItem.pgmId?.trim()   || '',
                    menuLvl     : originItem.menuLvl?.trim() || '',
                    menuOrdr    : originItem.menuOrdr.toString() || '',
                    useYn       : originItem.useYn,
                    isMobile    : originItem.isMobile,
                    isShow      : originItem.isShow
                };

                const currentCompare = {
                    upperMenuId : trimmedItem.upperMenuId     || null,
                    menuId      : trimmedItem.menuId?.trim()  || '',
                    menuNm      : trimmedItem.menuNm?.trim()  || '',
                    pgmId       : trimmedItem.pgmId?.trim()   || '',
                    menuLvl     : trimmedItem.menuLvl?.trim() || '',
                    menuOrdr    : trimmedItem.menuOrdr.toString() || '',
                    useYn       : trimmedItem.useYn,
                    isMobile    : trimmedItem.isMobile,
                    isShow      : trimmedItem.isShow
                };

                const isInfoDirty    = JSON.stringify(currentCompare) !== JSON.stringify(originCompare);

                //권한 비교
                const originalAuths  = menuAuthMap.filter(a => a.menuId === trimmedItem.menuId)
                                                  .map(a => a.authrtyId)
                                                  .sort();
                let currentAuths = [];
                if (trimmedItem.tmpAuths) {
                    // 방금 변경된 메뉴라면 이 값을 우선 사용
                    currentAuths = [...trimmedItem.tmpAuths].map(id => String(id).trim()).sort();
                } else {
                    // 그 외의 메뉴들은 기존 방식대로 맵에서 가져옴
                    const hasModifiedAuth = trimmedItem.menuId in modifiedAuthMap;
                    currentAuths = hasModifiedAuth 
                        ? [...modifiedAuthMap[trimmedItem.menuId]].map(id => String(id).trim()).sort() 
                        : originalAuths;
                }

                const isAuthDirty = JSON.stringify(originalAuths) !== JSON.stringify(currentAuths);

                // 마지막에 tmpAuths는 리덕스에 저장될 필요 없으니 제거해주면 더 깔끔
                const { tmpAuths, ...cleanItem } = trimmedItem; 
                return { ...cleanItem, dirty: isInfoDirty || isAuthDirty };
            }

            return trimmedItem;
        });
        
        //트리 구조 생성 (화면 표시용)
        const treeData = buildMenuTree(finalData);

        // 메뉴 목록 전체 갱신
        dispatch(Cod0102mAction.setMenuInfo(finalData));
        // 변환된 트리 데이터 갱신
        dispatch(Cod0102mAction.setMenuTree(treeData));
    };

    /**
     * * 기능   : 특정 부모 그룹의 menuOrdr를 1부터 재정렬하는 함수
     *   param : 전체메뉴리스트, 부모메뉴ID
     */
    const getReorderedList = (list: IMenuState[], targetUpperMenuId: string) => {
        // 해당 부모를 가진 자식들만 추출하여 현재 순서대로 정렬
        const children = list.filter(item => item.upperMenuId === targetUpperMenuId)
                             .sort((a, b) => Number(a.menuOrdr) - Number(b.menuOrdr));

        // 1부터 순차적으로 부여
        return list.map(item => {
            if (item.upperMenuId === targetUpperMenuId) {
                const newIndex = children.findIndex(c => c.menuId === item.menuId);
                return { ...item, menuOrdr: newIndex + 1, dirty: true };
            }
            return item;
        });
    };

    

    return (
        /* 화면 디자인 영역 */
        <Layout>
            <Content>
                <Collapse
                    bordered={false}
                    defaultActiveKey={['1']}
                    expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
                    className='site-collapse-custom-collapse'
                >
                    <Panel header='검색조건' key='1' className='site-collapse-custom-panel'>
                        <Row
                            align='middle'
                            justify='space-between'
                            style={{ marginTop: 10 }}
                        >
                            <Col flex='9.5'>
                                <Space>
                                    <label>메뉴권한ID</label>
                                    <Select
                                        style={{
                                            width: 200,
                                        }}
                                        optionFilterProp='label'
                                        options={authrtyIdList}
                                        onKeyUp={handleOnKeyUp}
                                        onChange={val => {
                                            dispatch(Cod0102mAction.setInquiryCnds({...inquiryCnds, authrtyId: val}))
                                        }}
                                        value={inquiryCnds.authrtyId}
                                    ></Select>
                                </Space>
                            </Col>
                            <Col flex='0.5'>
                                <Space>
                                    <Button name='btnSearch' className='background-color-primary-button' onClick={onClick} icon={<SearchOutlined/>} >
                                        조회
                                    </Button>
                                </Space>
                            </Col>
                        </Row>
                    </Panel>
                </Collapse>

                <Row>
                    <Col style={{ width: '30%', padding: 10 }}>
                        <Row justify="space-between" align="middle">
                            <Space>
                                <img className='title-icon' alt='' />
                                <Text strong>메뉴 목록</Text>
                            </Space>

                            <Space>
                                <Button name='btnAdd'      size="small" onClick={onClick} disabled={isFiltered} icon={<PlusOutlined     />}></Button>
                                <Button name='btnDelete'   size="small" onClick={onClick} disabled={isFiltered} icon={<MinusOutlined    />}></Button>
                                <Button name='btnMoveUp'   size="small" onClick={onClick} disabled={isFiltered} icon={<ArrowUpOutlined  />}></Button>
                                <Button name='btnMoveDown' size="small" onClick={onClick} disabled={isFiltered} icon={<ArrowDownOutlined/>}></Button>
                            </Space>
                        </Row>

                        <div style={{ marginTop: 10, height: '650px', border: '1px solid #eee', overflowY: 'auto' }}>
                            <Tree
                                showLine
                                prefixCls='menu-tree'
                                className='menu-tree'
                                defaultExpandAll
                                treeData={menuTree || []}
                                selectedKeys={[selectedMenuId]}
                                titleRender={(nodeData: any) => {
                                    // 노드를 그릴 때마다 체크
                                    const isNotUsed   = nodeData.useYn  === 'N';
                                    const isNotShowed = nodeData.isShow === 'N';

                                    return (
                                        <span className={isNotUsed ? 'menu-node-disabled' : isNotShowed ? 'menu-node-disabled' :''}>
                                            {nodeData.title}
                                        </span>
                                    );
                                }}
                                onSelect={(keys) => {
                                    //필수입력 체크
                                    if (selectedMenu) {
                                        if (!selectedMenu.menuId?.trim() || !selectedMenu.menuNm?.trim()) {
                                                const param = {
                                                    contents:  (!selectedMenu.menuId ? '[메뉴ID]' : '[메뉴명]') + ' 는(은) 필수 입력항목입니다.',
                                                };
                                            common.openInfo(param);
                                            return;
                                        }
                                    }

                                    if (keys.length > 0) {
                                        const menuId = keys[0] as string;
                                        dispatch(Cod0102mAction.setSelectedMenuId(menuId));

                                        //modifiedAuthMap에 menuId 키 없으면 초기진입
                                        if (!modifiedAuthMap[menuId]) {
                                            //서버에서 가져온 원본 데이터(menuAuthMap)에서 해당 메뉴 권한만
                                            const originalAuths = menuAuthMap.filter((item) => item.menuId === menuId)
                                                                             .map((item) => item.authrtyId);

                                            //modifiedAuthMap에 원본데이터를 초기값으로 세팅
                                            dispatch(Cod0102mAction.setModifiedAuthMap({ 
                                                menuId: menuId, 
                                                authIds: originalAuths 
                                            }));
                                        }
                                    }
                                }}
                            />
                        </div>
                    </Col>

                    <Col style={{ width: '70%', padding: 10, display: 'flex', flexDirection: 'column'}}>
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                            <Space>
                                <img className='title-icon' alt='' />
                                <Text strong>메뉴정보</Text>
                            </Space>

                            <div style={{ 
                                marginTop: 10, 
                                flex: 1, 
                                overflowY: 'auto'
                            }}>
                                <Descriptions bordered column={2} size='small'>
                                    <Descriptions.Item label='상위메뉴ID' span={1}>
                                        <Input
                                            // value={selectedMenu?.upperMenuNm === 'ROOT' ? '' : selectedMenu?.upperMenuId || ''}
                                            value={selectedMenu?.upperMenuId}
                                            disabled
                                        />
                                    </Descriptions.Item>

                                    <Descriptions.Item label='상위메뉴명' span={1}>
                                        <Input
                                            // value={selectedMenu?.upperMenuNm === 'ROOT' ? '' : selectedMenu?.upperMenuNm || ''}
                                            value={selectedMenu?.upperMenuNm}
                                            disabled
                                        />
                                    </Descriptions.Item>

                                    <Descriptions.Item label='메뉴ID' span={1}>
                                        <Input
                                            name        = 'menuId'
                                            placeholder = '신규등록 시 입력가능합니다.'
                                            maxLength   = {11}
                                            value       = {selectedMenu?.menuId || ''}
                                            disabled    = {!selectedMenu?.added}
                                            onChange    = {onChange}
                                        />
                                    </Descriptions.Item>

                                    <Descriptions.Item label='메뉴명' span={2}>
                                        <Input
                                            name        = 'menuNm'
                                            placeholder = '메뉴명을 입력하세요'
                                            maxLength   = {100}
                                            value       = {selectedMenu?.menuNm || ''}
                                            onChange    = {onChange}
                                        />
                                    </Descriptions.Item>
                                    
                                    <Descriptions.Item label='프로그램ID' span={2}>
                                        <Input
                                            name        = 'pgmId'
                                            placeholder = '프로그램ID를 입력하세요'
                                            maxLength   = {20}
                                            value       = {selectedMenu?.pgmId || ''}
                                            onChange    = {onChange}
                                        />
                                    </Descriptions.Item>

                                    <Descriptions.Item label='사용여부' span={2}>
                                        <Radio.Group
                                            // defaultValue="Y"
                                            name    = 'useYn'
                                            value   = {selectedMenu?.useYn || 'Y'}
                                            onChange= {onChange}
                                        >
                                            <Radio value="Y">Y</Radio>
                                            <Radio value="N">N</Radio>
                                        </Radio.Group>
                                    </Descriptions.Item>

                                    <Descriptions.Item label='모바일 사용여부' span={2}>
                                        <Radio.Group
                                            name    = 'isMobile'
                                            value   = {selectedMenu?.isMobile || 'N'}
                                            onChange= {onChange}
                                        >
                                            <Radio value="Y">Y</Radio>
                                            <Radio value="N">N</Radio>
                                        </Radio.Group>
                                    </Descriptions.Item>

                                    <Descriptions.Item label='노출여부' span={2}>
                                        <Radio.Group
                                            name    = 'isShow'
                                            value   = {selectedMenu?.isShow || 'Y'}
                                            onChange= {onChange}
                                        >
                                            <Radio value="Y">Y</Radio>
                                            <Radio value="N">N</Radio>
                                        </Radio.Group>
                                    </Descriptions.Item>
                                </Descriptions>
                            </div>
                        </div>

                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', marginTop: 10 }}>
                            <Space>
                                <img className='title-icon' alt='' />
                                <Text strong>메뉴권한관리</Text>
                            </Space>

                            <Row style={{ marginTop: 10 }}>
                                <Col span={24}>
                                    <Table 
                                        dataSource  = {authrtyIdList.filter(item => item.value !== '')}    // 전체 권한 마스터 리스트
                                        columns     = {authColumns()} 
                                        size        = 'small'
                                        rowKey      = 'authrtyId'            
                                        pagination  = {false} 
                                        style       = {{ width: '100%', height: 300 }} 
                                        scroll      = {{ y: 270 }}    // 높이 넘어가면 스크롤 생김
                                        rowSelection= {{
                                            type: 'checkbox',
                                            ...authRowSelection,      // 체크박스 로직 연결
                                        }}
                                    />
                                </Col>
                            </Row>
                        </div>
                    </Col>
            </Row>

            <Row justify='space-around' style={{marginTop: 15 }}>
                <Col style={{ marginRight: 15 }}>
                    <Space size='small'>
                        <Button name='btnReset' onClick={onClick} className='background-color-default-button'> 초기화 </Button>
                        <Button name='btnSave'  onClick={onClick} className='background-color-primary-button'> 저장   </Button>
                    </Space>
                </Col>
            </Row>
            
        </Content>
    </Layout>
);
};

export default Cod0102m;
